/**
 * Menu scope constants and utility functions
 */

export const VIEW_MODE = {
  ADMIN: "admin" as const,
  USER: "user" as const,
} as const;

export type ViewMode = typeof VIEW_MODE.ADMIN | typeof VIEW_MODE.USER;

export type MenuScope = ViewMode | ViewMode[];

/**
 * Check if a menu item should be shown in the given view
 * @param scope - The scope of the menu item (admin/user/both/undefined)
 * @param currentView - The current view mode
 * @returns true if the item should be shown
 */
export function shouldShowInView(
  scope: MenuScope | undefined,
  currentView: ViewMode
): boolean {
  // Strict filtering logic:
  // Admin View: Only show items explicitly scoped to ADMIN
  if (currentView === VIEW_MODE.ADMIN) {
    if (!scope) return false;
    if (Array.isArray(scope)) {
      return scope.includes(VIEW_MODE.ADMIN);
    }
    return scope === VIEW_MODE.ADMIN;
  }

  // User View: Show items explicitly scoped to USER or with undefined scope (default)
  if (!scope) return true;
  if (Array.isArray(scope)) {
    return scope.includes(VIEW_MODE.USER);
  }
  return scope === VIEW_MODE.USER;
}

/**
 * Check if a menu item has admin scope
 * @param scope - The scope of the menu item
 * @returns true if the item is admin-scoped
 */
export function hasAdminScope(scope: MenuScope | undefined): boolean {
  if (!scope) {
    return false;
  }

  if (typeof scope === "string") {
    return scope === VIEW_MODE.ADMIN;
  }

  if (Array.isArray(scope)) {
    return scope.includes(VIEW_MODE.ADMIN);
  }

  return false;
}
